import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  Phone, Mail, MapPin, Clock, ArrowUpRight, CheckCircle2, ExternalLink,
} from "lucide-react";
import { SITE, API_URL } from "@/lib/site";
import { scrollToElement } from "@/lib/scroll";
import { Reveal, SectionHead } from "@/components/motion";
import { useSEO } from "@/components/SEO";

const INITIAL = { nom: "", email: "", telephone: "", message: "" };

export default function Contact() {
  useSEO(
    "Contact & rendez-vous — BM MECHANICS Prilly (Lausanne, Vaud)",
    "Contactez BM MECHANICS à Prilly : téléphone 077 926 23 45, email, horaires et accès. Atelier mécanique auto, moto, scooter dans l'Ouest lausannois."
  );
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.nom.trim()) errs.nom = "Votre nom est requis";
    if (!form.message.trim()) errs.message = "Votre message est requis";
    if (!form.email.trim() && !form.telephone.trim()) errs.email = "Email ou téléphone requis";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Email invalide";
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Veuillez compléter les champs requis");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setSent(true);
      scrollToElement(document.querySelector('[data-testid="contact-form-card"]'));
      toast.success("Message envoyé à l'atelier");
    } catch {
      toast.error("Échec de l'envoi. Appelez-nous au " + SITE.phoneDisplay);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-[#121212] text-[#F6F3EB] pt-32 pb-16 md:pt-40 md:pb-20 texture-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#8C7B5D]">Contact</p>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight leading-[0.95]">
              Parlons de<br />votre véhicule
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Coordonnées */}
          <div className="lg:col-span-2 space-y-4">
            <Reveal>
              <a href={SITE.phoneHref} data-testid="contact-call-card" className="block bg-[#121212] text-[#F6F3EB] p-6 hover:bg-[#242529] transition-colors">
                <Phone className="h-5 w-5 text-[#8C7B5D]" />
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9A968C]">Téléphone</p>
                <p className="mt-1 font-display text-2xl font-bold tracking-tight">{SITE.phoneDisplay}</p>
                <p className="mt-2 text-sm text-[#9A968C]">Appel direct — réponse rapide</p>
              </a>
            </Reveal>
            <Reveal delay={0.05}>
              <a href={SITE.emailHref} data-testid="contact-email-card" className="block bg-white border border-[#D8D4CA] p-6 hover:border-[#121212] transition-colors">
                <Mail className="h-5 w-5 text-[#8C7B5D]" />
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#5A5D64]">Email</p>
                <p className="mt-1 font-display text-xl font-bold tracking-tight text-[#121212] break-all">{SITE.email}</p>
              </a>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="bg-white border border-[#D8D4CA] p-6" data-testid="contact-location-card">
                <MapPin className="h-5 w-5 text-[#8C7B5D]" />
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#5A5D64]">Localisation</p>
                <p className="mt-1 font-display text-xl font-bold tracking-tight text-[#121212]">Route de Cossonay 60</p>
                <p className="mt-1 text-sm text-[#4A4D53]">1008 Prilly · Ouest lausannois · Canton de Vaud</p>
                <a
                  href={SITE.mapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions-btn"
                  className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-[#121212] border-b-2 border-[#121212] pb-1 hover:text-[#8C7B5D] hover:border-[#8C7B5D] transition-colors"
                >
                  Itinéraire Google Maps <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="bg-white border border-[#D8D4CA] p-6" data-testid="contact-hours-card">
                <Clock className="h-5 w-5 text-[#8C7B5D]" />
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#5A5D64]">Horaires</p>
                <ul className="mt-3 space-y-2 text-sm text-[#121212]">
                  <li className="flex justify-between gap-4"><span>Lundi – Vendredi</span><span className="text-[#4A4D53]">08:00 – 12:00 · 13:30 – 18:00</span></li>
                  <li className="flex justify-between gap-4"><span>Samedi</span><span className="text-[#4A4D53]">09:00 – 12:00</span></li>
                  <li className="flex justify-between gap-4"><span>Dimanche</span><span className="text-[#4A4D53]">Fermé</span></li>
                </ul>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-[#9A968C]">
                  Urgence hors horaires ? Appelez, on trouve une solution
                </p>
              </div>
            </Reveal>
          </div>

          {/* Formulaire */}
          <div className="lg:col-span-3">
            <Reveal delay={0.05}>
              <div data-testid="contact-form-card" className="bg-white border border-[#D8D4CA] p-6 sm:p-8">
                {sent ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <span className="inline-flex h-14 w-14 items-center justify-center bg-[#121212] text-[#8C7B5D] rounded-full">
                      <CheckCircle2 className="h-7 w-7" />
                    </span>
                    <h2 className="mt-6 font-display text-2xl font-bold uppercase tracking-tight text-[#121212]">Message envoyé</h2>
                    <p className="mt-3 text-sm text-[#4A4D53] max-w-sm mx-auto">
                      Merci {form.nom.split(" ")[0]}. L'atelier vous recontacte dans les meilleurs délais.
                    </p>
                    <button
                      data-testid="contact-new-message-btn"
                      onClick={() => { setSent(false); setForm(INITIAL); }}
                      className="mt-6 font-mono text-[11px] uppercase tracking-[0.16em] text-[#121212] border-b-2 border-[#121212] pb-1 hover:text-[#8C7B5D] hover:border-[#8C7B5D] transition-colors"
                    >
                      Envoyer un autre message
                    </button>
                  </div>
                ) : (
                  <>
                    <SectionHead eyebrow="Message rapide" title="Écrivez-nous" />
                    <form onSubmit={submit} data-testid="contact-form" noValidate className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="c-nom" className="bm-label">Nom *</label>
                          <input id="c-nom" data-testid="contact-name-input" className="bm-input" placeholder="Votre nom" value={form.nom} onChange={set("nom")} />
                          {errors.nom && <p data-testid="contact-error-name" className="mt-1.5 text-xs text-[#A33E2B]">{errors.nom}</p>}
                        </div>
                        <div>
                          <label htmlFor="c-tel" className="bm-label">Téléphone</label>
                          <input id="c-tel" data-testid="contact-phone-input" className="bm-input" placeholder="07x xxx xx xx" inputMode="tel" value={form.telephone} onChange={set("telephone")} />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="c-email" className="bm-label">Email</label>
                        <input id="c-email" data-testid="contact-email-input" type="email" className="bm-input" placeholder="vous@exemple.ch" value={form.email} onChange={set("email")} />
                        {errors.email && <p data-testid="contact-error-email" className="mt-1.5 text-xs text-[#A33E2B]">{errors.email}</p>}
                      </div>
                      <div>
                        <label htmlFor="c-message" className="bm-label">Message *</label>
                        <textarea id="c-message" data-testid="contact-message-input" className="bm-input min-h-[140px] resize-y" placeholder="Votre question, demande de rendez-vous…" value={form.message} onChange={set("message")} />
                        {errors.message && <p data-testid="contact-error-message" className="mt-1.5 text-xs text-[#A33E2B]">{errors.message}</p>}
                      </div>
                      <button
                        type="submit"
                        data-testid="contact-submit-btn"
                        disabled={sending}
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#121212] text-[#F6F3EB] font-mono text-xs uppercase tracking-[0.18em] px-6 py-4 hover:bg-[#8C7B5D] hover:text-[#121212] transition-colors disabled:opacity-50 disabled:pointer-events-none"
                      >
                        {sending ? "Envoi en cours…" : "Envoyer le message"} <ArrowUpRight className="h-4 w-4" />
                      </button>
                    </form>
                  </>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-24" data-testid="contact-map">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="border border-[#D8D4CA] bg-white p-2">
              <iframe
                title="Carte — BM MECHANICS, Prilly"
                src={SITE.mapsEmbed}
                className="w-full aspect-[16/9] sm:aspect-[21/9] grayscale"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9A968C]">
              Route de Cossonay 60, 1008 Prilly — à proximité de Lausanne, Renens, Chavannes-près-Renens, Épalinges
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
